/**
 * Kickard Audio — Web Audio API procedural SFX.
 *
 * Closed-alpha cleanup (Phase 3.5.5):
 *   - All volumes lowered to the spec ranges in PRD section "SFX cleanup".
 *   - Card sounds (draw/place/remove) are now subtle felt swipes / soft
 *     taps with low-pass filtering — no plastic clicks, no slot-machine
 *     feel, no harsh high-frequency content.
 *   - The 9 essential SFX keys from the spec are exposed both via legacy
 *     method names AND new spec-aligned aliases (uiTap, cardDraw,
 *     cardPlace, cardRemove, attackWhoosh, blockClick, goalSting,
 *     jokerShimmer, warningTick) so audio swaps later are a one-line
 *     change.
 *   - `warningTick` is internally rate-limited so the timer can call it
 *     freely from a ticking interval without spamming the user.
 *   - No background music, no chants, no crowd ambience.
 */

let _ctx: AudioContext | null = null;
let _enabled = true;

function ctx(): AudioContext {
  if (_ctx) return _ctx;
  const Ctor = (window.AudioContext || (window as any).webkitAudioContext);
  _ctx = new Ctor();
  return _ctx;
}

// `warningTick` rate-limit: at most one tick per 700 ms regardless of
// how aggressively the timer fires. Per PRD only meaningful thresholds
// (5/3/2/1 s) should actually call this method — this gate is just a
// safety net.
let _lastWarningTick = 0;

// Soft felt swipe — used for card_draw.
function _felt(duration: number, gain: number, peakHz: number) {
  if (!_enabled) return;
  const c = ctx();
  const now = c.currentTime;
  const bufferSize = Math.max(1, Math.floor(c.sampleRate * duration));
  const buf = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buf.getChannelData(0);
  // Pink-ish noise with a soft attack-decay envelope.
  for (let i = 0; i < bufferSize; i++) {
    const t = i / bufferSize;
    const env = Math.min(t * 4, 1) * Math.pow(1 - t, 1.4);
    data[i] = (Math.random() * 2 - 1) * env;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  // Heavy low-pass so the high-frequency hiss is gone (no harsh click).
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = peakHz;
  lp.Q.value = 0.5;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(lp).connect(g).connect(c.destination);
  src.start(now);
}

// Soft muted tap — used for card_place / ui_tap.
function _tap(gain: number, freqHz: number, durationS: number) {
  if (!_enabled) return;
  const c = ctx();
  const now = c.currentTime;
  const o = c.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(freqHz, now);
  o.frequency.exponentialRampToValueAtTime(Math.max(40, freqHz * 0.5), now + durationS);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(gain, now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, now + durationS);
  o.connect(g).connect(c.destination);
  o.start(now);
  o.stop(now + durationS + 0.02);
}

export const sounds = {
  setEnabled(enabled: boolean) { _enabled = enabled; },
  isEnabled() { return _enabled; },
  /** Must be called after user gesture for autoplay-restricted browsers. */
  unlock() {
    try {
      const c = ctx();
      if (c.state === 'suspended') c.resume();
    } catch {}
  },

  // ---- Subtle UI / card sounds (lower-volume cleanup) ----

  uiTap()      { _tap(0.08, 700, 0.04); },
  cardDraw()   { _felt(0.14, 0.06, 1100); },
  cardPlace()  { _tap(0.06, 220, 0.07); },
  cardRemove() { _felt(0.10, 0.04, 900); },

  // ---- Legacy method names — preserved so existing call sites keep
  //      working untouched. They route to the subtle variants above. ----
  draw()  { this.cardDraw(); },
  place() { this.cardPlace(); },

  // ---- Combat / attack / block ----
  attackWhoosh() {
    if (!_enabled) return;
    const c = ctx();
    const now = c.currentTime;
    const bufferSize = c.sampleRate * 0.18;
    const buf = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      const t = i / bufferSize;
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 2) * Math.min(1, t * 6);
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 480;
    bp.Q.value = 0.8;
    const g = c.createGain();
    g.gain.value = 0.14;
    src.connect(bp).connect(g).connect(c.destination);
    src.start(now);
  },
  /** Legacy alias from the v1 procedural set. */
  attackWin() { this.attackWhoosh(); },

  blockClick() {
    if (!_enabled) return;
    const c = ctx();
    const now = c.currentTime;
    const o = c.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(280, now);
    o.frequency.exponentialRampToValueAtTime(140, now + 0.08);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.14, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
    o.connect(g).connect(c.destination);
    o.start(now);
    o.stop(now + 0.12);
  },

  // ---- Score / save / joker / victory / defeat ----
  goalSting() {
    if (!_enabled) return;
    const c = ctx();
    const now = c.currentTime;
    const bufferSize = c.sampleRate * 0.7;
    const buf = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      const env = Math.min(1, i / (c.sampleRate * 0.08)) * (1 - i / bufferSize);
      data[i] = (Math.random() * 2 - 1) * env;
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 700;
    bp.Q.value = 0.5;
    const g = c.createGain();
    g.gain.value = 0.22;
    src.connect(bp).connect(g).connect(c.destination);
    src.start(now);

    [440, 554, 659, 880].forEach((freq, i) => {
      const o = c.createOscillator();
      o.type = 'triangle';
      o.frequency.value = freq;
      const og = c.createGain();
      og.gain.setValueAtTime(0, now + i * 0.07);
      og.gain.linearRampToValueAtTime(0.10, now + i * 0.07 + 0.02);
      og.gain.exponentialRampToValueAtTime(0.001, now + i * 0.07 + 0.35);
      o.connect(og).connect(c.destination);
      o.start(now + i * 0.07);
      o.stop(now + i * 0.07 + 0.4);
    });
  },
  goal() { this.goalSting(); },

  save() {
    if (!_enabled) return;
    const c = ctx();
    const now = c.currentTime;
    const o = c.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(280, now);
    o.frequency.exponentialRampToValueAtTime(140, now + 0.22);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.14, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    o.connect(g).connect(c.destination);
    o.start(now);
    o.stop(now + 0.3);
  },

  jokerShimmer() {
    if (!_enabled) return;
    const c = ctx();
    const now = c.currentTime;
    [400, 380, 340].forEach((freq, i) => {
      const o = c.createOscillator();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(freq, now);
      o.frequency.exponentialRampToValueAtTime(freq * 0.45, now + 0.55);
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, now + i * 0.04);
      g.gain.linearRampToValueAtTime(0.12, now + i * 0.04 + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
      o.connect(g).connect(c.destination);
      o.start(now + i * 0.04);
      o.stop(now + 0.75);
    });
  },
  joker() { this.jokerShimmer(); },

  victory() {
    if (!_enabled) return;
    const c = ctx();
    const now = c.currentTime;
    [523, 659, 784, 1047].forEach((f, i) => {
      const o = c.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0, now + i * 0.18);
      g.gain.linearRampToValueAtTime(0.18, now + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.18 + 0.4);
      o.connect(g).connect(c.destination);
      o.start(now + i * 0.18);
      o.stop(now + i * 0.18 + 0.45);
    });
  },

  defeat() {
    if (!_enabled) return;
    const c = ctx();
    const now = c.currentTime;
    [400, 320, 220, 150].forEach((f, i) => {
      const o = c.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0, now + i * 0.16);
      g.gain.linearRampToValueAtTime(0.14, now + i * 0.16 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.16 + 0.45);
      o.connect(g).connect(c.destination);
      o.start(now + i * 0.16);
      o.stop(now + i * 0.16 + 0.5);
    });
  },

  warningTick() {
    if (!_enabled) return;
    const now = Date.now();
    if (now - _lastWarningTick < 700) return;
    _lastWarningTick = now;
    const c = ctx();
    const t = c.currentTime;
    const o = c.createOscillator();
    o.type = 'square';
    o.frequency.value = 1100;
    const g = c.createGain();
    g.gain.setValueAtTime(0.09, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    o.connect(g).connect(c.destination);
    o.start(t);
    o.stop(t + 0.06);
  },
  tick() { this.warningTick(); },
};
